import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { EmptyStateMessage, Tile } from '../../detail/tabs/Tile';
import { ConsultationContentType } from '../../detail/tabs/contentTypes';
import { ConsultationStatusEnum } from '../../enums/consultationStatusEnum';
import { ConsultationService, sharedApiClient } from '../../fetcher';
import { useNotifications, CustomDropdown } from '@medprompt/shared';
import { MeasurementTypeEnum } from '../../enums/measurementTypeEnum';
import AddMeasurementForm from '../../detail/AddMeasurementForm';
import {
  BLOOD_PRESSURE_DISPLAY_NAME,
  bundleBloodPressureReadings,
  getDisplayRowId,
  getDisplayRowIds,
  getDisplayRowUnit,
  getMeasurementId,
  isBloodPressurePair,
  isDisplayRowSelected,
} from '../../detail/measurements/bundleBloodPressureReadings';
import { BloodPressureValueDisplay, BloodPressureValueEditor } from '../../detail/measurements/BloodPressureValue';

const consultationShouldShowContent = (field, status) => {
  return field !== undefined || status === ConsultationStatusEnum.READY.value;
};

// Enum for measurement comparators matching backend MeasurementComparator
const MeasurementComparator = {
  EQ: { value: 'EQ', display: '=', symbol: '' },
  GT: { value: 'GT', display: '>', symbol: '>' },
  LT: { value: 'LT', display: '<', symbol: '<' }
};

// Helper function to get comparator symbol for display
const getComparatorSymbol = (comparator) => {
  if (!comparator || comparator === 'EQ' || !(comparator in MeasurementComparator)) {
    return '';
  }
  return MeasurementComparator[comparator].symbol + ' ';
};

const MeasurementsTab = ({ 
  consultation, 
  isEditable, 
  isRegenerationEnabled, 
  handleRegenerationClick, 
  regenerateService,
  onConsultationUpdate,
  shouldBundleBloodPressureReadings = false,
}) => {
  const consultationManager = useMemo(() => new ConsultationService(sharedApiClient), []);
  const { addNotification } = useNotifications();
  
  const [editingMeasurementId, setEditingMeasurementId] = useState(null);
  const [editableValue, setEditableValue] = useState('');
  const [editableSecondaryValue, setEditableSecondaryValue] = useState('');
  const [editableUnit, setEditableUnit] = useState('');
  const [editableComparator, setEditableComparator] = useState('EQ');
  const [isComparatorDropdownOpen, setIsComparatorDropdownOpen] = useState(false);
  const [showAddMeasurementForm, setShowAddMeasurementForm] = useState(false);
  
  const measurementValueInputRef = useRef(null);
  const measurementEditContainerRef = useRef(null);

  const status = consultation?.status;
  const consultationId = consultation?.consultationId;
  const measurements = consultation?.measurements;

  const displayRows = useMemo(
    () => bundleBloodPressureReadings(measurements, shouldBundleBloodPressureReadings),
    [measurements, shouldBundleBloodPressureReadings]
  );

  // Focus value input when editing starts
  useEffect(() => {
    if (editingMeasurementId && measurementValueInputRef.current) {
      measurementValueInputRef.current.focus();
      measurementValueInputRef.current.select();
    }
  }, [editingMeasurementId]);

  const applyLocalMeasurementUpdates = useCallback((ids, updater) => {
    const idSet = new Set(ids);
    const updatedMeasurements = measurements?.map((measurement, index) => {
      const id = getMeasurementId(measurement, index);
      return idSet.has(id) ? updater(measurement, id) : measurement;
    });

    if (onConsultationUpdate) {
      onConsultationUpdate({ ...consultation, measurements: updatedMeasurements });
    }
  }, [measurements, consultation, onConsultationUpdate]);

  const persistSelection = useCallback((ids, isChecked) => {
    if (!isEditable || !consultationId) return;
    const isSelected = isChecked ? true : null;
    const isDeselected = !isChecked ? true : null;
    ids.forEach((measurementId) => {
      consultationManager.updateConsultationMeasurement(consultationId, { measurementId, isSelected, isDeselected })
        .catch(error => {
          console.error('Error updating measurement selection:', error);
          addNotification(`measurement-select-error-${measurementId}`, 'Failed to update measurement selection', { isError: true });
        });
    });
  }, [isEditable, consultationId, consultationManager, addNotification]);

  const handleDisplayRowSelectionChange = useCallback((row, isChecked) => {
    if (!isEditable || !consultationId) return;
    const ids = getDisplayRowIds(row);
    applyLocalMeasurementUpdates(ids, (measurement) => ({ ...measurement, isSelected: isChecked }));
    persistSelection(ids, isChecked);
  }, [isEditable, consultationId, applyLocalMeasurementUpdates, persistSelection]);

  // Handler for selecting all measurements
  const handleSelectAllMeasurements = useCallback((e) => {
    if (!isEditable || !consultationId) return;
    
    const checked = e.target.checked;
    
    const updatedMeasurements = measurements?.map((measurement) => {
      return { ...measurement, isSelected: checked };
    });
    
    const updatedConsultation = { ...consultation, measurements: updatedMeasurements };
    
    if (onConsultationUpdate) {
      onConsultationUpdate(updatedConsultation);
    }
    
    measurements?.forEach((measurement, index) => {
      persistSelection([getMeasurementId(measurement, index)], checked);
    });
  }, [isEditable, consultationId, measurements, consultation, onConsultationUpdate, persistSelection]);

  const clearEditState = useCallback(() => {
    setEditingMeasurementId(null);
    setEditableValue('');
    setEditableSecondaryValue('');
    setEditableUnit('');
    setEditableComparator('EQ');
    setIsComparatorDropdownOpen(false);
  }, []);

  const handleDisplayRowClick = useCallback((row) => {
    const rowId = getDisplayRowId(row);
    if (!isEditable || editingMeasurementId === rowId) return;

    setEditingMeasurementId(rowId);
    setIsComparatorDropdownOpen(false);
    if (isBloodPressurePair(row)) {
      setEditableValue(row.systolic.measurement.value?.toString() || '');
      setEditableSecondaryValue(row.diastolic.measurement.value?.toString() || '');
      setEditableUnit(getDisplayRowUnit(row));
      setEditableComparator('EQ');
      return;
    }

    setEditableValue(row.measurement.value?.toString() || '');
    setEditableSecondaryValue('');
    setEditableUnit(row.measurement.unit || '');
    setEditableComparator(row.measurement.comparator || 'EQ');
  }, [isEditable, editingMeasurementId]);

  const persistMeasurementFields = useCallback((measurementId, fields) => {
    if (!consultationId) return;
    consultationManager.updateConsultationMeasurement(consultationId, { measurementId, ...fields })
      .catch(error => {
        console.error('Error updating measurement value/unit:', error);
        addNotification(`measurement-save-error-${measurementId}`, 'Failed to save measurement changes', { isError: true });
      });
  }, [consultationId, consultationManager, addNotification]);

  const handleDisplayRowBlur = useCallback((row) => {
    if (!consultationId) return;

    if (isBloodPressurePair(row)) {
      const originalSystolic = row.systolic.measurement.value?.toString() || '';
      const originalDiastolic = row.diastolic.measurement.value?.toString() || '';
      const originalUnit = getDisplayRowUnit(row);
      const hasChanges =
        editableValue !== originalSystolic ||
        editableSecondaryValue !== originalDiastolic ||
        editableUnit !== originalUnit;

      if (hasChanges && editableValue.trim() && editableSecondaryValue.trim()) {
        const systolicValue = parseFloat(editableValue);
        const diastolicValue = parseFloat(editableSecondaryValue);
        const unit = editableUnit || row.systolic.measurement.unit;

        applyLocalMeasurementUpdates(
          [row.systolic.id, row.diastolic.id],
          (measurement, id) => ({
            ...measurement,
            value: id === row.systolic.id
              ? (isNaN(systolicValue) ? measurement.value : systolicValue)
              : (isNaN(diastolicValue) ? measurement.value : diastolicValue),
            unit: unit || measurement.unit,
          })
        );

        persistMeasurementFields(row.systolic.id, {
          value: isNaN(systolicValue) ? null : systolicValue,
          unit: editableUnit || null,
        });
        persistMeasurementFields(row.diastolic.id, {
          value: isNaN(diastolicValue) ? null : diastolicValue,
          unit: editableUnit || null,
        });
      }

      clearEditState();
      return;
    }

    const measurement = row.measurement;
    const measurementId = row.id;
    const hasChanges = 
      editableValue !== (measurement.value?.toString() || '') ||
      editableUnit !== (measurement.unit || '') ||
      editableComparator !== (measurement.comparator || 'EQ');

    if (hasChanges && editableValue.trim()) {
      const value = parseFloat(editableValue);
      applyLocalMeasurementUpdates([measurementId], (current) => ({
        ...current,
        value: isNaN(value) ? current.value : value,
        unit: editableUnit || current.unit,
        comparator: editableComparator || 'EQ',
      }));

      persistMeasurementFields(measurementId, {
        value: isNaN(value) ? null : value,
        unit: editableUnit || null,
        comparator: editableComparator || 'EQ',
      });
    }
    
    clearEditState();
  }, [consultationId, editableValue, editableSecondaryValue, editableUnit, editableComparator, applyLocalMeasurementUpdates, persistMeasurementFields, clearEditState]);

  const handleMeasurementKeyDown = useCallback((e, row) => {
    if (e.key === 'Escape') {
      if (isBloodPressurePair(row)) {
        setEditableValue(row.systolic.measurement.value?.toString() || '');
        setEditableSecondaryValue(row.diastolic.measurement.value?.toString() || '');
        setEditableUnit(getDisplayRowUnit(row));
      } else {
        setEditableValue(row.measurement.value?.toString() || '');
        setEditableUnit(row.measurement.unit || '');
        setEditableComparator(row.measurement.comparator || 'EQ');
      }
      setEditingMeasurementId(null);
      setIsComparatorDropdownOpen(false);
    }
  }, []);

  // Save and exit edit mode when clicking outside the edit container
  useEffect(() => {
    if (!editingMeasurementId) return;

    const handlePointerDownOutside = (event) => {
      if (measurementEditContainerRef.current?.contains(event.target)) {
        return;
      }

      const row = displayRows.find((displayRow) => getDisplayRowId(displayRow) === editingMeasurementId);
      if (!row) return;

      handleDisplayRowBlur(row);
    };

    document.addEventListener('mousedown', handlePointerDownOutside);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside);
  }, [editingMeasurementId, displayRows, handleDisplayRowBlur]);

  const handleAddMeasurement = useCallback((newMeasurement) => {
    consultationManager.addMeasurements(consultationId, [newMeasurement])
      .then(() => {
        setShowAddMeasurementForm(false);
      })
      .catch(error => {
        console.error('Error adding measurement:', error);
        addNotification(`measurement-add-error-${consultationId}`, 'Failed to add measurement', { isError: true });
      });
  }, [consultationId, consultationManager, addNotification]);

  const renderMeasurementCode = (measurement) => {
    if (!measurement.code) return null;
    return (
      <div className="measurement-code">
        <div className="measurement-code-value">
          {measurement.code.code}
        </div>
        {measurement.code.terms && measurement.code.terms.length > 0 && (
          <div className="measurement-code-term">
            {measurement.code.terms[0]}
          </div>
        )}
      </div>
    );
  };

  const renderMeasurements = () => {
    if (!measurements || measurements.length === 0) {
      return <EmptyStateMessage message="No measurements found" />;
    }

    return displayRows.map((row) => {
      const rowId = getDisplayRowId(row);
      const isPair = isBloodPressurePair(row);
      const measurement = isPair ? row.systolic.measurement : row.measurement;
      const typeLabel = isPair
        ? BLOOD_PRESSURE_DISPLAY_NAME
        : (MeasurementTypeEnum[measurement.type]?.displayName || measurement.type);
      
      return (
        <div key={rowId} className="document-ai-item measurement-item">
          <div className="measurement-details">
            <div className="measurement-type code-id">
              {typeLabel}
            </div>
            
            <div className="measurement-value-container">
              <div className="measurement-value-row">
                {editingMeasurementId === rowId ? (
                  isPair ? (
                    <BloodPressureValueEditor
                      containerRef={measurementEditContainerRef}
                      systolicInputRef={measurementValueInputRef}
                      systolicValue={editableValue}
                      diastolicValue={editableSecondaryValue}
                      unit={editableUnit}
                      onSystolicChange={setEditableValue}
                      onDiastolicChange={setEditableSecondaryValue}
                      onUnitChange={setEditableUnit}
                      onKeyDown={(e) => handleMeasurementKeyDown(e, row)}
                    />
                  ) : (
                    <div 
                      ref={measurementEditContainerRef}
                      className="measurement-edit-container"
                      onKeyDown={(e) => handleMeasurementKeyDown(e, row)}
                    >
                      <CustomDropdown
                        selected={MeasurementComparator[editableComparator]?.display || '='}
                        options={Object.values(MeasurementComparator)}
                        isOpen={isComparatorDropdownOpen}
                        toggleDropdown={() => setIsComparatorDropdownOpen(!isComparatorDropdownOpen)}
                        onSelect={(option) => {
                          setEditableComparator(option.value);
                          setIsComparatorDropdownOpen(false);
                        }}
                        className="measurement-comparator-dropdown"
                        width="80px"
                      />
                      <input
                        ref={measurementValueInputRef}
                        type="number"
                        step="any"
                        value={editableValue}
                        onChange={(e) => setEditableValue(e.target.value)}
                        className="measurement-value-input"
                        placeholder="Value"
                      />
                      <input
                        type="text"
                        value={editableUnit}
                        onChange={(e) => setEditableUnit(e.target.value)}
                        className="measurement-unit-input"
                        placeholder="Unit"
                      />
                    </div>
                  )
                ) : isPair ? (
                  <BloodPressureValueDisplay
                    systolicMeasurement={row.systolic.measurement}
                    diastolicMeasurement={row.diastolic.measurement}
                    canEdit={isEditable}
                    onClick={() => handleDisplayRowClick(row)}
                  />
                ) : (
                  <div 
                    className={`measurement-value-display ${isEditable ? 'measurement-value-editable' : ''}`}
                    onClick={() => handleDisplayRowClick(row)}
                    onMouseMove={(e) => {
                      const tooltip = e.currentTarget.querySelector('.edit-tooltip');
                      if (tooltip) {
                        tooltip.style.left = `${e.clientX + 10}px`;
                        tooltip.style.top = `${e.clientY + 10}px`;
                      }
                    }}
                  >
                    {getComparatorSymbol(measurement.comparator)}{measurement.value} {measurement.unit}
                    {isEditable && <div className="edit-tooltip">Click to edit measurement</div>}
                  </div>
                )}
              </div>
            </div>

            {isPair ? (
              <>
                {renderMeasurementCode(row.systolic.measurement)}
                {renderMeasurementCode(row.diastolic.measurement)}
              </>
            ) : (
              renderMeasurementCode(measurement)
            )}
          </div>
          <input 
            type="checkbox" 
            className="outcome-checkbox measurement-checkbox" 
            id={`checkbox_${rowId}`} 
            checked={isDisplayRowSelected(row)}
            onChange={(e) => handleDisplayRowSelectionChange(row, e.target.checked)}
            disabled={!isEditable}
          />
        </div>
      );
    });
  };

  const availableTypes = useMemo(() =>
    Object.keys(MeasurementTypeEnum).filter(type =>
      !measurements?.some(m => m.type === type)
    ),
    [measurements]
  );

  const consultationAsDoc = useMemo(() => ({
    ...consultation,
    documentId: consultationId
  }), [consultation, consultationId]);

  const measurementsTile = (
    <Tile
      doc={consultation}
      entityId={consultationId}
      contentType={ConsultationContentType.MEASUREMENTS_STATUS}
      title="Patient Measurements"
      condition={consultationShouldShowContent(measurements, status)}
      isEnabled={isRegenerationEnabled ? isRegenerationEnabled() : false}
      onRegenerateClick={handleRegenerationClick}
      regenerateService={regenerateService}
      showContentOnError
      shouldShowError={() => !(measurements?.length > 0)}
      headerContent={
        measurements && measurements.length > 0 && isEditable && 
          <input 
            type="checkbox" 
            className="code-checkbox measurement-checkbox" 
            onChange={handleSelectAllMeasurements}
            disabled={!isEditable}
          />
      }
    >
      {isEditable && availableTypes.length > 0 && !showAddMeasurementForm && (
        <button
          className="add-measurement-button"
          onClick={() => setShowAddMeasurementForm(true)}
        >
          + Add Measurement
        </button>
      )}
      <div className="measurements-list">
        {showAddMeasurementForm && (
          <AddMeasurementForm
            doc={consultationAsDoc}
            onConfirm={handleAddMeasurement}
            onCancel={() => setShowAddMeasurementForm(false)}
          />
        )}
        {renderMeasurements()}
      </div>
    </Tile>
  );

  return (
    <div className="document-detail-tiles">
      {measurementsTile}
    </div>
  );
};

export default MeasurementsTab;
